
import { Component, Vue, Prop } from "vue-property-decorator";
import { API_TDocumentCard, API_TFile } from "@/models/document_cards";
import { SMCases, SMDocuments, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
// import pdf from "vue-pdf";
import NbNewUploader from "@/components/NewFileUploader/NbNewUploader.vue";

import {
  OUTDATED_FILE_STATUS,
  NOT_RELEVANT_FILE_STATUS,
  DEFAULT_FILE_TYPE,
  COS_FILE_TYPE,
  IELTS_FILE_TYPE,
  PTE_FILE_TYPE,
} from "@/constants/files";

interface IUploadPopup {
  isOpen: boolean;
  file: API_TFile | null;
}

const MESSAGES = {
  notification: {
    allFilesSaved: {
      title: "Сongrats! All files have been uploaded",
    },
  },
};

@Component({
  components: {
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    NbNewUploader,
  },
})
export default class DocumentCardUpload extends Vue {
  @Prop({ type: Object }) readonly documentCard?: API_TDocumentCard;
  @Prop({ type: Boolean, default: false }) readonly error?: boolean;
  @Prop({ type: Boolean, default: false }) readonly canUploadFile?: boolean;
  @Prop({ type: Boolean, default: false }) readonly canArchiveFile?: boolean;
  @Prop({ type: Boolean, default: false }) readonly canDeleteFile?: boolean;
  @Prop({ type: Boolean, default: false }) readonly isUploadDisabled?: boolean;
  @Prop({ type: String, default: DEFAULT_FILE_TYPE }) readonly fileType?: string;

  files: File[] = [];

  uploadError = false;

  isLoading = false;

  archivePopup: IUploadPopup = {
    isOpen: false,
    file: null,
  };

  deletePopup: IUploadPopup = {
    isOpen: false,
    file: null,
  };

  isUploaderOpen = false;

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get activeUploadedFiles() {
    return SMDocuments.activeUploadedFiles.filter((file) => file.status !== OUTDATED_FILE_STATUS);
  }

  get activeUploadedFilesOutdated() {
    return SMDocuments.activeUploadedFiles.filter((file) => file.status === OUTDATED_FILE_STATUS);
  }

  get archivedUploadedFiles() {
    return SMDocuments.archivedUploadedFiles;
  }

  get uploadErrorMessage() {
    if (this.fileType === COS_FILE_TYPE) return this.$tc("upload.errorMessage.cos");

    if (this.fileType === IELTS_FILE_TYPE) return this.$tc("upload.errorMessage.ielts");

    if (this.fileType === PTE_FILE_TYPE) return this.$tc("upload.errorMessage.pte");

    return this.$tc("upload.errorMessage.default");
  }

  get documentCardDecision() {
    return this.documentCard?.decision;
  }

  openUploaderPopup() {
    this.isUploaderOpen = false;

    this.$nextTick(() => {
      this.isUploaderOpen = true;
    });
  }

  /* 
  getDate converts ISO date string "2024-09-30T11:33:15.191579Z" to en-GB "DD/MM/yyyy"
  */
  getDate(isoDate: string): string {
    if (!isoDate) return "";

    const date = new Date(isoDate);

    const formattedDate = date.toLocaleDateString("en-GB");

    if (formattedDate === "Invalid Date") return "";

    return formattedDate;
  }

  getOutdatedStatus(status: string) {
    return status === NOT_RELEVANT_FILE_STATUS || status === OUTDATED_FILE_STATUS;
  }

  async downloadUploadedFile(file: API_TFile) {
    SMDocuments.setRouteFullPath(this.$route.fullPath);

    const data = await SMDocuments.getFileData(file);

    if (!data) return;

    SMDocuments.downloadFile(data);
  }

  askToArchiveFile(file: API_TFile) {
    this.archivePopup.file = file;
    this.archivePopup.isOpen = true;
  }

  onArchivationCancel() {
    this.archivePopup.isOpen = false;
    this.archivePopup.file = null;
  }

  askToDeleteFile(file: API_TFile) {
    this.deletePopup.file = file;
    this.deletePopup.isOpen = true;
  }

  onDeleteCancel() {
    this.deletePopup.isOpen = false;
    this.deletePopup.file = null;
  }

  async viewUploadedFile(file: API_TFile) {
    SMDocuments.setRouteFullPath(this.$route.fullPath);

    const data = await SMDocuments.getFileData(file);

    SMDocuments.unsetRouteFullPath();

    if (!data) return;

    const { link, name } = data;

    this.openPDFDialog(link, name);
  }

  initDescription = false;

  async onUploaderSave() {
    if (!this.documentCard) return;

    if (this.isEnglishExamFile) return;

    try {
      this.isLoading = true;

      this.uploadError = false;

      const formData = new FormData();

      this.files.forEach((file) => {
        formData.append("file", file);
      });

      const url =
        SMUser.isExpert || SMUser.isCandidate
          ? API_ROUTES.CASE_DOCUMENT_FILES(this.caseId, this.documentCard.ID)
          : API_ROUTES.DOCUMENT_COMPANY(this.documentCard.ID);

      const { data } = await this.axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      this.$emit("update", data);
    } catch (error) {
      this.uploadError = true;

      this.files = [];
    } finally {
      this.isLoading = false;

      this.files = [];
    }
  }

  get showUpload() {
    if (!this.isEnglishExamFile) return true;

    return !this.activeUploadedFiles?.length;
  }

  get isEnglishExamFile() {
    return this.fileType === IELTS_FILE_TYPE || this.fileType === PTE_FILE_TYPE;
  }

  uploaderLoading = false;

  uploaderErrorText = "";

  async onFileUploaded() {
    if (!this.isEnglishExamFile) return;

    if (!this.documentCard) return;

    try {
      this.uploaderLoading = true;

      this.uploaderErrorText = "";

      const formData = new FormData();

      this.files.forEach((file) => {
        formData.append("file", file);
      });

      const url =
        SMUser.isExpert || SMUser.isCandidate
          ? API_ROUTES.CASE_DOCUMENT_FILES(this.caseId, this.documentCard.ID)
          : API_ROUTES.DOCUMENT_COMPANY(this.documentCard.ID);

      const { data } = await this.axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      this.$emit("update", data);

      this.isUploaderOpen = false;
    } catch (error) {
      this.uploaderErrorText = this.uploadErrorMessage;
    } finally {
      this.uploaderLoading = false;

      this.files = [];
    }
  }

  async uploaderSaveMethod(file) {
    const { caseId, documentCard } = this;

    const formData = new FormData();

    formData.append("file", file);

    let url =
      SMUser.isExpert || SMUser.isCandidate
        ? API_ROUTES.CASE_DOCUMENT_FILES(caseId, documentCard.ID)
        : API_ROUTES.DOCUMENT_COMPANY(documentCard.ID);

    url += "?preview=true";

    const { data } = await this.axios.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    });

    return Promise.resolve({ data });
  }

  get isCompliance() {
    return !this.caseId || !SMCases.caseType;
  }

  async uploaderConfirmMethod(ids, uploadData) {
    const { caseId, documentCard } = this;

    const url = this.isCompliance
      ? API_ROUTES.COMPLIANCE_DOCUMENT_SAVE_MULTIPLE(documentCard.ID)
      : API_ROUTES.CASE_DOCUMENT_SAVE_MULTIPLE(caseId, documentCard.ID);

    const { status } = await this.axios.post(url, ids);

    this.$emit("update", uploadData);

    if (uploadData) {
      this.$emit("update", uploadData);
    }

    return Promise.resolve({ data: ids, status });
  }

  uploaderSuccessMethod(success) {
    if (success) {
      this.$root.$emit("show-notification", {
        title: MESSAGES.notification.allFilesSaved.title,
        type: "success",
        congrats: true,
        showImage: true,
      });
    }
  }

  confirmSuccessMethod(payload) {
    const { success } = payload;

    if (!success) return;

    // this.$emit("update");

    this.isUploaderOpen = false;
  }

  onUploadCancel() {
    this.files = [];
  }

  async deleteFile() {
    this.deletePopup.isOpen = false;

    if (!this.deletePopup.file) return;

    const isDeleted = await SMDocuments.deleteFile(this.deletePopup.file);

    if (!isDeleted) return;

    this.$emit("update");
  }

  async archiveFile() {
    if (!this.documentCard || !this.archivePopup.file) return;

    try {
      const url = API_ROUTES.ARCHIVE_DOCUMENT_FILE(this.documentCard.ID, this.archivePopup.file.ID);

      await this.axios.put(url);

      this.$emit("update");
    } finally {
      this.archivePopup.isOpen = false;
    }
  }

  openPDFDialog(link: string, name: string) {
    this.$emit("openPDFDialog", { link, name });
  }
}
